import { useState, useEffect, useMemo } from "react"
import { useLocation } from "react-router-dom"
import qs from "query-string"
import {
  removeKeys,
  removeNulls,
  sanitizeObjectAttributes,
  trimObject,
} from "~utils/utils"
import { ROLES } from "~rbac/constants/rbac-roles"
import { PERK_REDEMPTION_STATUS } from "~constants/status"
import { filtersProps } from "~components/dashboard/RedemptionWidget"

export function useFilters(loggedUserRoles: string[]) {
  const { search, pathname } = useLocation()

  const defaultStatus = useMemo(() => {
    const pendingRoles = [
      ROLES.ROLE_PERK_CENTRAL_USER_CCL_AIDA,
      ROLES.ROLE_PERK_CENTRAL_USER_CCL_PO_CRUISES_AUS,
      ROLES.ROLE_PERK_CENTRAL_USER_CCL_COSTA,
    ]
    return loggedUserRoles.some(r => pendingRoles.includes(r))
      ? PERK_REDEMPTION_STATUS.PENDING_EXTERNAL_VALIDATION
      : PERK_REDEMPTION_STATUS.PENDING_EXTERNAL_MANUAL_VALIDATION
  }, [loggedUserRoles])

  const parseQuery = (searchString: string): Partial<filtersProps> => {
    const params = qs.parse(searchString, { arrayFormat: "bracket" })
    return {
      bookingId: params.bookingId as string,
      bookingLastName: params.bookingLastName as string,
      bookingFirstName: params.bookingFirstName as string,
      status: (params.status as string) || "",
      userEmail: params.userEmail as string,
      userId: params.userId as string,
      proofOfOwnershipName: params.proofOfOwnershipName as string,
      updatedByEmail: params.updatedByEmail as string,
      createdAtFrom: params.createdAtFrom as string,
      createdAtTo: params.createdAtTo as string,
      bookingEventDateFrom: params.bookingEventDateFrom as string,
      bookingEventDateTo: params.bookingEventDateTo as string,
      sort: params.sort as string,
    }
  }

  const initializeFilters = (): filtersProps => {
    return {
      bookingId: "",
      bookingLastName: "",
      bookingFirstName: "",
      status: defaultStatus,
      userEmail: "",
      userId: "",
      proofOfOwnershipName: "",
      updatedByEmail: "",
      createdAtFrom: "",
      createdAtTo: "",
      bookingEventDateFrom: "",
      bookingEventDateTo: "",
      sort: "",
    }
  }

  const [filters, setFilters] = useState<filtersProps>(() => {
    const parsed = parseQuery(search)
    const trimmed = trimObject(parsed)
    const hasAny = Object.values(trimmed).some(
      v => typeof v === "string" && v.trim() !== "",
    )
    return hasAny ? (trimmed as filtersProps) : initializeFilters()
  })

  const appliedQty = useMemo(() => {
    const cleaned = removeNulls(
      sanitizeObjectAttributes(
        removeKeys(filters, ["bookingEventDateFrom", "createdAtFrom", "sort"]),
      ),
    )
    return Object.keys(cleaned).length
  }, [filters])

  useEffect(() => {
    const newQuery = qs.stringify(filters, { arrayFormat: "bracket" })
    const newSearch = newQuery ? `?${newQuery}` : ""
    if (newSearch !== search) {
      window.history.replaceState(null, "", `${pathname}${newSearch}`)
    }
  }, [filters, pathname, search])

  return { filters, setFilters, appliedQty }
}
